import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { FormattedMessage } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import { Link } from 'dibs-elements/exports/Link';
import { trackEvent } from 'dibs-tracking';
import { getSrcsetString, getSrcsetUrl } from '../../utils/srcSet';

import styles from './SbSharedContentModuleBannerTiles.scss';

const DEFAULT_MAX_IMG_WIDTH = 1018;

function fireTracking() {
    trackEvent({
        category: 'ecommerce',
        action: 'promotion click',
        label: 'collections banner tiles',
    });
}

function SbSharedContentModuleBannerTilesComponent({
    browseContentModule,
    renderImage = ({ src, alt, className, srcSet, sizes }) => (
        <img src={src} alt={alt} className={className} srcSet={srcSet} sizes={sizes} />
    ),
}) {
    const { title, description, mainImage, tiles, termsLink } = browseContentModule;

    if (!title) {
        return null;
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.moduleWrapper}>
                <div className={styles.imageWrapper}>
                    {renderImage({
                        src: `${mainImage}?width=${DEFAULT_MAX_IMG_WIDTH}`,
                        alt: title,
                        className: styles.image,
                        srcSet: getSrcsetString(mainImage),
                        sizes: `
                            (max-width: 569px) 100vw,
                            (max-width: 768px) 50vw,
                            60vw
                        `,
                    })}
                </div>
                <div className={styles.textWrapper}>
                    <HeadingLevel>
                        {Heading => (
                            <>
                                <Heading className={styles.header}>{title}</Heading>
                                <div
                                    className={styles.bodyText}
                                    dangerouslySetInnerHTML={{ __html: description }}
                                />
                            </>
                        )}
                    </HeadingLevel>
                    {termsLink && (
                        <Link href={termsLink} className={styles.termsLink}>
                            <FormattedMessage
                                id="abf.SbSharedContentModuleBannerTiles.termsLink"
                                defaultMessage="Terms & Conditions"
                            />
                        </Link>
                    )}
                </div>
            </div>
            <div className={styles.tilesWrapper}>
                {tiles.map(
                    (tile, index) =>
                        tile.title && (
                            <SeoLink
                                ariaLabel={tile.title}
                                dataTn={`browse-cms-tile-${index}`}
                                onClick={fireTracking}
                                className={styles.tile}
                                key={`browse-cms-tile-${index}`}
                                linkData={tile?.linkData}
                            >
                                {renderImage({
                                    src: tile.imageUrl,
                                    alt: tile.title,
                                    className: styles.tileImage,
                                    srcSet: `
                                        ${getSrcsetUrl(tile.imageUrl, 72)} 1x,
                                        ${getSrcsetUrl(tile.imageUrl, 144)} 2x,
                                    `,
                                })}
                                <span className={styles.tileTitle}>{tile.title}</span>
                            </SeoLink>
                        )
                )}
            </div>
        </div>
    );
}

SbSharedContentModuleBannerTilesComponent.propTypes = {
    browseContentModule: PropTypes.object.isRequired,
    renderImage: PropTypes.function,
};

export const SbSharedContentModuleBannerTiles = createFragmentContainer(
    SbSharedContentModuleBannerTilesComponent,
    {
        browseContentModule: graphql`
            fragment SbSharedContentModuleBannerTiles_browseContentModule on ContentModuleBannerTiles {
                title
                description
                mainImage
                termsLink
                tiles {
                    imageUrl
                    title
                    linkData {
                        ...SeoLink_linkData
                    }
                }
            }
        `,
    }
);
